import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Trash2, Plus, Shield } from "lucide-react";

/**
 * Roles & Permissions Matrix — a role × permission tick-off card.
 * Backend endpoints: GET/POST/DELETE /api/roles
 */
export default function RolesMatrix() {
  const [roles, setRoles] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [newRole, setNewRole] = useState({ code: "", name: "", description: "", permissions: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/roles");
    setRoles(r.data.roles); setBuckets(r.data.buckets);
    if (!selectedRole && r.data.roles.length) setSelectedRole(r.data.roles[0].id);
  };
  useEffect(() => { load(); }, []);

  const role = roles.find(r => r.id === selectedRole);
  const perms = new Set(role?.permissions || []);

  const toggle = (p) => {
    if (!role) return;
    const next = new Set(perms);
    next.has(p) ? next.delete(p) : next.add(p);
    setRoles(rs => rs.map(x => x.id === role.id ? { ...x, permissions: Array.from(next), _dirty: true } : x));
  };
  const toggleBucket = (bucketPerms, allOn) => {
    if (!role) return;
    const next = new Set(perms);
    bucketPerms.forEach(p => allOn ? next.delete(p) : next.add(p));
    setRoles(rs => rs.map(x => x.id === role.id ? { ...x, permissions: Array.from(next), _dirty: true } : x));
  };

  const save = async () => {
    if (!role) return;
    setBusy(true);
    try {
      await api.post("/roles", { code: role.code, name: role.name, description: role.description, permissions: role.permissions, is_active: role.is_active }, { params: { id: role.id } });
      toast.success(`Role "${role.name}" saved — ${role.permissions.length} permissions`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const addRole = async () => {
    if (!newRole.name) return toast.error("Role name required");
    await api.post("/roles", newRole);
    toast.success("Role added");
    setNewRole({ code: "", name: "", description: "", permissions: [] });
    await load();
  };
  const delRole = async () => {
    if (!role || role.is_system) return toast.error("System roles cannot be deleted");
    if (!window.confirm(`Delete role "${role.name}"? Users with this role will need reassignment.`)) return;
    await api.delete(`/roles/${role.id}`);
    setSelectedRole(null);
    toast.success("Deleted");
    await load();
  };

  const BUCKET_LABELS = {
    o2c: "Order to Cash", p2p: "Procure to Pay", banking: "Banking",
    manufacturing: "Manufacturing", dealership: "Dealership 360",
    compliance: "Compliance", mis: "MIS & Reports",
  };

  return (
    <div className="card-sharp p-5 space-y-4" data-testid="roles-matrix">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">👥 Roles &amp; Permissions</div>
          <div className="text-xs text-muted-foreground mt-1">Tick which features each role can access across the 7 buckets.</div>
        </div>
      </div>

      <div className="grid grid-cols-[240px,1fr] gap-4">
        {/* Role list */}
        <div className="border rounded-sm bg-muted/30" data-testid="role-list">
          <div className="text-[10px] font-mono uppercase text-muted-foreground p-2 border-b">Roles · {roles.length}</div>
          <div className="max-h-[480px] overflow-y-auto">
            {roles.map(r => (
              <button key={r.id} onClick={() => setSelectedRole(r.id)}
                className={`w-full text-left px-3 py-2 border-b hover:bg-accent/5 transition ${selectedRole === r.id ? "bg-accent/10 border-l-2 border-l-accent" : ""}`}
                data-testid={`role-${r.code}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{r.name}</div>
                  {r.is_system && <Shield className="w-3 h-3 text-muted-foreground" title="System role" />}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono">{r.code} · {r.permissions?.length || 0} perms</div>
              </button>
            ))}
          </div>
          <div className="border-t p-2 space-y-1 bg-white">
            <Input value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} placeholder="New role name" className="rounded-sm h-8 text-xs" data-testid="new-role-name" />
            <Input value={newRole.code} onChange={(e) => setNewRole({ ...newRole, code: e.target.value })} placeholder="code (optional)" className="rounded-sm h-8 text-xs font-mono" />
            <Button size="sm" onClick={addRole} className="w-full h-7 rounded-sm bg-accent hover:bg-accent/90 text-white text-xs" data-testid="add-role"><Plus className="w-3 h-3 mr-1" /> Add role</Button>
          </div>
        </div>

        {/* Permission matrix for selected role */}
        {role ? (
          <div className="space-y-3" data-testid="perm-matrix">
            <div className="flex items-center justify-between border-b pb-2">
              <div>
                <div className="text-lg font-semibold flex items-center gap-2">{role.name} {role.is_system && <span className="text-[9px] bg-muted px-1.5 py-0.5 rounded-sm font-mono uppercase">system</span>}</div>
                <Input value={role.description || ""} onChange={(e) => setRoles(rs => rs.map(x => x.id === role.id ? { ...x, description: e.target.value, _dirty: true } : x))} className="rounded-sm h-7 text-xs mt-1 max-w-md" placeholder="Description" />
              </div>
              <div className="flex gap-2">
                {!role.is_system && <Button size="sm" variant="ghost" onClick={delRole} className="text-red-600" data-testid="del-role"><Trash2 className="w-3 h-3" /></Button>}
                {role._dirty && <Button size="sm" onClick={save} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-role"><Save className="w-3 h-3 mr-1" /> Save</Button>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {Object.entries(buckets).map(([bucket, perms_list]) => {
                const on = perms_list.filter(p => perms.has(p)).length;
                const allOn = on === perms_list.length;
                const anyOn = on > 0;
                return (
                  <div key={bucket} className="border rounded-sm p-3 bg-white" data-testid={`bucket-${bucket}`}>
                    <label className="flex items-center gap-2 cursor-pointer border-b pb-2 mb-2">
                      <input type="checkbox" checked={allOn} ref={(el) => el && (el.indeterminate = !allOn && anyOn)} onChange={() => toggleBucket(perms_list, allOn)} data-testid={`bucket-toggle-${bucket}`} />
                      <span className="text-xs font-medium">{BUCKET_LABELS[bucket] || bucket}</span>
                      <span className="ml-auto text-[10px] font-mono text-muted-foreground">{on}/{perms_list.length}</span>
                    </label>
                    <div className="space-y-1">
                      {perms_list.map(p => (
                        <label key={p} className="flex items-center gap-2 cursor-pointer text-[11px] font-mono" data-testid={`perm-${p}`}>
                          <input type="checkbox" checked={perms.has(p)} onChange={() => toggle(p)} />
                          <span className={perms.has(p) ? "" : "text-muted-foreground"}>{p}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground flex items-center justify-center border border-dashed rounded-sm">Select a role from the list</div>
        )}
      </div>
    </div>
  );
}
